import moment from 'moment-timezone';

import { addMinutesToClock, formatClock12 } from '../../helpers';

const CODE_IN_TEXT = /\b(99\d{3}|96\d{3}|J0\d{3})\b/;

const CODE_ALIASES = { 99214: '99215', 99204: '99205' };

export const VISIT_KINDS = [
  'followUp',
  'consultation',
  'infusion',
  'assessment',
  'carePlan',
  'other',
];

export const visitCode = ({ cptCode, visitType } = {}) => {
  const raw = `${cptCode || ''}`.trim() || CODE_IN_TEXT.exec(`${visitType || ''}`)?.[1] || null;
  if (!raw) return null;
  return CODE_ALIASES[raw] ?? raw;
};

export const shortVisitCode = (code) =>
  /^\d{5}$/.test(`${code}`) ? `${code}`.slice(2) : `${code}`;

const KIND_BY_CODE = {
  99205: 'consultation',
  99215: 'followUp',
  99483: 'carePlan',
  96116: 'assessment',
  96132: 'assessment',
  96136: 'assessment',
  96138: 'assessment',
};

const kindFromName = (visitType) => {
  const name = `${visitType || ''}`.toLowerCase();
  if (/infusion/.test(name)) return 'infusion';
  if (/99483|care plan/.test(name)) return 'carePlan';
  if (/new patient|new member|consult/.test(name)) return 'consultation';
  if (/assessment|evaluation|memory|testing|results visit/.test(name)) return 'assessment';
  if (/follow|check|ongoing care|99215/.test(name)) return 'followUp';
  return 'other';
};

export const visitKind = (code, visitType) => {
  if (code && /^J0\d{3}$/.test(code)) return 'infusion';
  return KIND_BY_CODE[code] ?? kindFromName(visitType);
};

const TRAILING_CODE = /\s*\((?:99\d{3}|96\d{3}|J0\d{3})\)\s*$/;

export const visitLabelWithoutCode = (label) =>
  `${label || ''}`.replace(TRAILING_CODE, '') || `${label || ''}`;

export const zoneAbbr = (timeZone, date, time) => {
  if (!timeZone) return '';
  const zoned = moment.tz(
    `${date || ''} ${time || '12:00'}`.trim(),
    ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD'],
    timeZone,
  );
  const abbreviation = zoned.isValid() ? zoned.format('z') : '';
  return abbreviation && !abbreviation.startsWith('+') && abbreviation !== 'UTC'
    ? abbreviation
    : '';
};

export const clockRange = (start, end, zone) => {
  const a = formatClock12(start);
  const b = formatClock12(end);
  if (!a || !b) return a || '';
  const range = a.slice(-2) === b.slice(-2) ? `${a.slice(0, -3)}–${b}` : `${a}–${b}`;
  return zone ? `${range} ${zone}` : range;
};

export const keepTimeTogether = (text) =>
  `${text || ''}`.replace(
    /(\d{1,2}:\d{2}) (AM|PM)(?: ([A-Z]{2,5})\b)?/g,
    (_, clock, meridiem, zone) =>
      zone ? `${clock}\u00A0${meridiem}\u00A0${zone}` : `${clock}\u00A0${meridiem}`,
  );

export const clinicVisitTimes = (appt) => {
  const hasClinicTime = Boolean(appt?.departmentLocalTime && appt?.departmentTimezone);
  const start = (hasClinicTime ? appt.departmentLocalTime : appt?.time) || '';
  const date = (hasClinicTime ? appt.departmentLocalDate : null) || appt?.date;
  const timeZone = hasClinicTime
    ? appt.departmentTimezone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const minutes = Number(appt?.durationMinutes) > 0 ? Number(appt.durationMinutes) : 30;
  const end = addMinutesToClock(start, minutes);
  const zone = zoneAbbr(timeZone, date, start);
  const localStart = appt?.time || '';
  const localDate = appt?.date || date;
  const localZone = zoneAbbr(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    localDate,
    localStart,
  );
  const localRange = localStart
    ? clockRange(localStart, addMinutesToClock(localStart, minutes), localZone)
    : '';
  return {
    start,
    end,
    minutes,
    zone,
    date,
    timeZone,
    range: clockRange(start, end, zone),
    localRange,
  };
};
